import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Repeat, CalendarDays } from 'lucide-react';
import { AREE } from '../../constants/areas';
import Checkbox from '../nav/Checkbox';
import TaskTextEditor from '../nav/TaskTextEditor';
import DateAssignPopover from '../nav/DateAssignPopover';
import DemoteToAzioneButton from '../nav/DemoteToAzioneButton';
import MovedBadge from '../nav/MovedBadge';
import { labelGiorno } from '../../lib/periodFormat';

const FADE_TRANS = 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * MitSection — lista MIT (Most Important Task) raggruppata.
 *
 * Raggruppamento:
 *   - gran === 'giorno'   → per area (T3, top-border colorato)
 *   - gran === 'settimana'→ per giorno della settimana (7 secchi Lun→Dom)
 *   - altrimenti          → per data
 *
 * Props:
 *   - gran, filteredMit, mit (length), filtersActive
 *   - filteredMitByArea, filteredMitByData, filteredMitBySevenDays
 *   - krById
 *   - collapsed map + isCollapsed(kind,key) + toggleCollapsed(kind,key)
 *   - onToggle(id, next)
 *   - onChipAreaClick(area)
 *   - onEditRecurrence(parent) — apre RecurrenceEditor
 */
export default function MitSection({
  gran,
  filteredMit, mit, filtersActive,
  filteredMitByArea, filteredMitByData, filteredMitBySevenDays,
  krById,
  isCollapsed, toggleCollapsed,
  onToggle, onChipAreaClick, onEditRecurrence,
  onUpdateText, onDeleteTask, onAssignDate, onDemoted,
}) {
  const isGiorno = gran === 'giorno';
  const isSettimana = gran === 'settimana';
  const groups = isGiorno
    ? filteredMitByArea
    : isSettimana && filteredMitBySevenDays
      ? filteredMitBySevenDays
      : filteredMitByData;

  // Visibilità sezione: ha senso mostrare il titolo anche in settimana
  // per avere i 7 secchi vuoti come placeholder.
  if (filteredMit.length === 0 && !isSettimana) return null;

  return (
    <section className="mb-12" data-testid="section-mit">
      <h2 className="font-display text-xl md:text-2xl tracking-tight mb-4">
        Most Important Task (MIT) · {filteredMit.length}
        {filtersActive && mit.length > filteredMit.length ? ` di ${mit.length}` : ''}
      </h2>

      {groups.map(([key, items]) => {
        const done = items.filter((m) => m.fatto).length;
        const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
        const areaMeta = isGiorno
          ? (AREE[key] || { color: 'var(--qp-text-soft)', label: key === 'senza-area' ? 'Senza area' : key })
          : null;
        const headerLabel = isGiorno
          ? (areaMeta.label || (key === 'senza-area' ? 'Senza area' : key))
          : (key === 'senza-data' ? 'Senza data' : labelGiorno(key));
        const showHeader = isGiorno || isSettimana || groups.length > 1;

        // In settimana mostriamo anche i giorni vuoti (items.length === 0)
        if (items.length === 0 && !isSettimana) return null;

        return (
          <div
            key={key}
            className="mb-5"
            data-testid={`mit-group-${key}`}
            style={
              isGiorno
                ? { borderTop: `3px solid ${areaMeta.color}`, paddingTop: 10 }
                : isSettimana
                  ? { borderTop: '1px solid var(--qp-border)', paddingTop: 10 }
                  : undefined
            }
          >
            {showHeader && (
              <button
                type="button"
                onClick={() => toggleCollapsed('mit', key)}
                className="w-full flex items-baseline gap-3 mb-2 pb-1 border-b border-line hover:bg-linesoft/40 transition-colors text-left"
                data-testid={`mit-group-header-${key}`}
                aria-expanded={!isCollapsed('mit', key)}
              >
                <span className="shrink-0 text-mute" aria-hidden>
                  {isCollapsed('mit', key)
                    ? <ChevronRight size={12} strokeWidth={1.5} />
                    : <ChevronDown size={12} strokeWidth={1.5} />}
                </span>
                <span
                  className="font-mono text-[11px] uppercase tracking-[0.2em]"
                  style={{ color: isGiorno ? areaMeta.color : '#171717' }}
                >
                  {headerLabel}
                </span>
                <span className="font-mono text-[10px] text-soft tabular-nums" data-testid={`mit-group-stat-${key}`}>
                  {done}/{items.length}{items.length > 0 ? ` · ${pct}%` : ''}
                </span>
                {items.length > 0 && (
                  <span className="flex-1 h-1 bg-linesoft relative overflow-hidden ml-1" aria-hidden="true">
                    <span
                      className="absolute inset-y-0 left-0 transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: pct === 100 ? 'var(--qp-success)' : (isGiorno ? areaMeta.color : 'var(--qp-ink)'),
                      }}
                    />
                  </span>
                )}
              </button>
            )}
            {!isCollapsed('mit', key) && (items.length === 0 ? (
              <p className="font-body text-xs text-mute italic px-1 py-2" data-testid={`mit-empty-${key}`}>
                — nessun MIT —
              </p>
            ) : (
              <ul className="divide-y divide-linesoft">
                {items.map((m) => {
                  const kr = m.key_result_id ? krById[m.key_result_id] : null;
                  const areaKey = (kr?.area || '').toLowerCase();
                  const meta = AREE[areaKey] || null;
                  const textStyle = {
                    textDecoration: m.fatto ? 'line-through' : 'none',
                    color: m.fatto ? 'var(--qp-text-mute)' : undefined,
                    opacity: m.fatto ? 0.5 : 1,
                    transition: FADE_TRANS,
                  };
                  return (
                    <li key={m.id} className="group py-3 flex items-center gap-3" data-testid={`row-mit-${m.id}`}>
                      <Checkbox
                        checked={m.fatto}
                        onToggle={(next) => onToggle(m.id, next, m)}
                        size={18}
                        label={m.testo}
                        testid={`toggle-mit-${m.id}`}
                      />
                      <MovedBadge id={m.id} testid={`moved-badge-mit-${m.id}`} />
                      {(m.recurrence_rule || m.is_occurrence) ? (
                        <button
                          type="button"
                          onClick={() => onEditRecurrence && onEditRecurrence({
                            id: m.parent_id || m.id.split('::')[0],
                            kind: 'mit',
                            testo: m.testo,
                            recurrence_rule: m.recurrence_rule,
                            recurrence_end: m.recurrence_end,
                          })}
                          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-ink bg-ink text-canvas hover:bg-ok hover:border-ok transition-colors"
                          title={m.is_occurrence ? 'Occorrenza ricorrente · modifica serie' : 'Modifica ricorrenza'}
                          aria-label="Modifica ricorrenza"
                          data-testid={`mit-recurrence-btn-${m.id}`}
                        >
                          <Repeat size={10} strokeWidth={1.8} />
                        </button>
                      ) : !m.is_occurrence && (
                        <button
                          type="button"
                          onClick={() => onEditRecurrence && onEditRecurrence({
                            id: m.id,
                            kind: 'mit',
                            testo: m.testo,
                            recurrence_rule: null,
                            recurrence_end: null,
                          })}
                          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-transparent text-mute hover:border-line hover:text-ink opacity-0 group-hover:opacity-100 transition-all"
                          title="Rendi ricorrente"
                          aria-label="Rendi ricorrente"
                          data-testid={`mit-recurrence-add-${m.id}`}
                        >
                          <Repeat size={10} strokeWidth={1.5} />
                        </button>
                      )}
                      {meta && !isGiorno && (
                        <button
                          type="button"
                          onClick={() => onChipAreaClick(areaKey)}
                          className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0 underline decoration-dotted underline-offset-2 hover:no-underline hover:opacity-70 cursor-pointer"
                          style={{ borderColor: meta.color, color: meta.color }}
                          title={`Filtra per ${meta.label}`}
                          data-testid={`periodo-mit-area-chip-${m.id}`}
                        >
                          {meta.label.slice(0, 3)}
                        </button>
                      )}
                      {onUpdateText ? (
                        <TaskTextEditor
                          kind="mit"
                          id={m.id}
                          isOccurrence={!!m.is_occurrence}
                          text={m.testo}
                          onSaveText={(nv) => onUpdateText('mit', m.id, nv)}
                          onDelete={onDeleteTask ? () => onDeleteTask('mit', m.id) : null}
                          textStyle={textStyle}
                        />
                      ) : (
                        <span
                          className="flex-1 font-body text-sm"
                          style={textStyle}
                        >
                          {m.testo}
                        </span>
                      )}
                      {kr && (
                        <Link
                          to={`/obiettivo/${kr.obiettivo_id}`}
                          className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-accent shrink-0"
                          data-testid={`mit-kr-link-${m.id}`}
                        >
                          → {kr.titolo}
                        </Link>
                      )}
                      {!isGiorno && !isSettimana && groups.length === 1 && m.data && (
                        <span className="font-mono text-[10px] tabular-nums text-mute shrink-0">{m.data}</span>
                      )}
                      {onAssignDate && !m.is_occurrence && (
                        <DateAssignPopover
                          kind="mit"
                          currentValue={m.data || ''}
                          onSave={(nv) => onAssignDate(m.id, m.data || '', nv, 'mit')}
                          side="left"
                          testid={`mit-date-popover-${m.id}`}
                        >
                          <button
                            type="button"
                            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border transition-colors ${
                              m.data
                                ? 'border-ink text-ink hover:bg-ink hover:text-canvas'
                                : 'border-line text-mute hover:border-ink hover:text-ink'
                            }`}
                            title={m.data
                              ? `MIT fissata al ${m.data} · clicca per cambiare data`
                              : 'Sposta MIT a un altro giorno'}
                            aria-label="Sposta MIT"
                            data-testid={`mit-date-btn-${m.id}`}
                          >
                            <CalendarDays size={11} strokeWidth={1.6} />
                          </button>
                        </DateAssignPopover>
                      )}
                      {onDemoted && !m.is_occurrence && (
                        <DemoteToAzioneButton
                          mitId={m.id}
                          onDemoted={onDemoted}
                          testid={`periodo-demote-azione-${m.id}`}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            ))}
          </div>
        );
      })}
    </section>
  );
}
